import React from 'react';
import styled from 'styled-components';
import { Languages, Globe } from 'lucide-react';
import GoogleTranslateService from '../services/googleTranslateService';

const LanguageSelector = styled.div`
  position: absolute;
  bottom: 60px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 6px;
  padding: 8px;
  backdrop-filter: blur(10px);
  z-index: 100;
`;

const LanguageButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.active ? '#007bff' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  min-width: 120px;
  background: ${props => props.active ? 'rgba(0, 123, 255, 0.3)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.4);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LanguageDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 6px;
  padding: 4px 0;
  margin-bottom: 8px;
  min-width: 160px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const LanguageOption = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: white;
  padding: 8px 12px;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(0, 123, 255, 0.3);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const VideoLanguageSelector = ({ 
  project, 
  currentLanguage, 
  onLanguageChange, 
  isOpen, 
  onToggle 
}) => {
  const availableLanguages = [];
  
  // Add original transcript language
  if (project.transcript && project.transcript.length > 0) {
    availableLanguages.push({
      code: 'original',
      name: project.transcriptionLanguage || 'English',
      type: 'transcript',
      icon: <Languages />
    });
  }
  
  // Add all translation languages from new format
  const translations = project.translations || {};
  
  // Legacy support: if old translation exists, add it
  if (project.translation && project.translationLanguage && !translations[project.translationLanguage]) {
    translations[project.translationLanguage] = project.translation;
  }
  
  // Add all available translations
  Object.keys(translations).forEach(languageCode => {
    if (translations[languageCode] && translations[languageCode].length > 0) {
      // Get language name from Google Translate service
      const googleService = new GoogleTranslateService();
      const supportedLanguages = googleService.getSupportedLanguages();
      const languageInfo = supportedLanguages.find(l => l.code === languageCode);
      
      availableLanguages.push({
        code: languageCode,
        name: languageInfo ? languageInfo.name : languageCode.toUpperCase(),
        type: 'translation',
        icon: <Globe />
      });
    }
  });

  if (availableLanguages.length === 0) return null;

  const getCurrentLanguageName = () => {
    const current = availableLanguages.find(lang => lang.code === currentLanguage);
    return current ? current.name : availableLanguages[0]?.name || 'Original';
  };

  const getCurrentLanguageIcon = () => {
    const current = availableLanguages.find(lang => lang.code === currentLanguage);
    return current ? current.icon : <Languages />;
  };

  return (
    <LanguageSelector>
      {isOpen && (
        <LanguageDropdown>
          {availableLanguages.map((language) => (
            <LanguageOption
              key={language.code}
              className={currentLanguage === language.code ? 'active' : ''}
              onClick={() => {
                onLanguageChange(language.code);
                onToggle();
              }}
            >
              {language.icon}
              {language.name}
              <span style={{ fontSize: '10px', opacity: 0.7, marginLeft: 'auto' }}>
                {language.type === 'transcript' ? 'Original' : 'Translated'}
              </span>
            </LanguageOption>
          ))}
        </LanguageDropdown>
      )}
      
      <LanguageButton 
        onClick={onToggle}
        active={currentLanguage !== 'original'}
      >
        {getCurrentLanguageIcon()}
        {getCurrentLanguageName()}
      </LanguageButton>
    </LanguageSelector>
  );
};

export default VideoLanguageSelector; 